import { useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { capitalize } from "@/lib/formato"
import { Alert, Stack, Typography } from "@mui/material"
import ItemsBody from "./items-body"
import ItemsFooter from "./items-footer"
import { useLeitorResumo } from "./use-leitor-resumo"
import ScreenHeader from "../components/screen-header"
import type { AppDispatch, RootState } from "@/store"
import { useGetLojaAtualQuery, MENSAGEM_BLOQUEIO_TOTEM } from "@/api"
import { resetarView, trocarView } from "../navegacao-kiosk"
import AvisoInatividade from "@/components/totem/aviso-inatividade"
import { useTaxaServicoTotem } from "./use-taxa-servico-totem"
import { reverterTaxaServicoSePendente } from "./reverter-taxa-servico"

const INATIVIDADE_MS = 120_000

function ItemsScreen() {
    const dispatch = useDispatch<AppDispatch>()
    const modeloIdentificador = useSelector((state: RootState) => state.selfServiceKiosk.modeloIdentificador)
    const identificadorBuscado = useSelector((state: RootState) => state.selfServiceKiosk.identificadorBuscado)
    const rotuloPrincipal = capitalize(modeloIdentificador)
    const rotuloOposto = modeloIdentificador === 'COMANDA' ? 'Mesa' : 'Comanda'
    const order = useSelector((state: RootState) => state.selfServiceKiosk.order)
    const hasMultipleOrders = useSelector((state: RootState) => state.selfServiceKiosk.hasMultipleOrders)
    const { data: loja } = useGetLojaAtualQuery()
    const taxa = useTaxaServicoTotem(loja?.configuracao.taxaServicoDispensavel ?? false)
    const bloqueado = order?.bloqueadoNoTotem === true
    const leitorLigado = loja?.configuracao.totemCheckoutLeitorCodigoBarras === true
    const leitor = useLeitorResumo(leitorLigado && !bloqueado && !taxa.alterando)
    const motivo = order?.motivoBloqueio

    const onClickBack = () => {
        reverterTaxaServicoSePendente(dispatch, order)

        if (hasMultipleOrders) {
            trocarView(dispatch, { currentView: 'VIEW_MULTIPLE_ORDERS', order: null, hasMultipleOrders: false })
        } else {
            trocarView(dispatch, { currentView: 'VIEW_IDENTIFIER', order: null })
        }
    }

    const voltarAoInicio = useCallback(() => {
        reverterTaxaServicoSePendente(dispatch, order)
        resetarView(dispatch)
    }, [dispatch, order])

    return (
        <Stack sx={{ width: '100%', height: '100vh', boxSizing: 'border-box', overflow: 'hidden' }}>
            <ScreenHeader
                title={`${rotuloPrincipal} ${identificadorBuscado}`}
                subTitle={order?.identificadorSecundario ? `${rotuloOposto} ${order.identificadorSecundario}` : 'Sem Mesa'}
                onClickBack={onClickBack}
                voltarDesabilitado={taxa.alterando}
            />

            {bloqueado && (
                <Alert severity="warning" variant="filled" sx={{ mx: 2, mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {motivo ? MENSAGEM_BLOQUEIO_TOTEM[motivo] : 'Esta conta não pode ser alterada no totem.'}
                    </Typography>
                </Alert>
            )}

            <ItemsBody taxa={taxa} bloqueado={bloqueado} leituras={leitor.leituras} dicaLeitor={leitorLigado && !bloqueado} leitorPausado={leitor.pausado} />

            <ItemsFooter bloqueado={taxa.alterando || bloqueado} />

            <AvisoInatividade ativo msInatividade={INATIVIDADE_MS} aoExpirar={voltarAoInicio} />
        </Stack>
    )
}

export default ItemsScreen
